import {
  createFileRoute,
  useNavigate,
  type MakeRouteMatchUnion,
} from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import {
  AppError,
  Button,
  Card,
  Checkbox,
  Form,
  Loader,
  TextInput,
  toast,
} from "@bfc-software/pallet";
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { apiClient } from "@bfc-software/bfc-web-ui/lib/webhooks-api-client";
import palletOverrides from "@bfc-software/bfc-web-ui/pallet-overrides.module.css";

import styles from "./new.module.css";

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/internal/whs/$location/_layout/webhooks/endpoints/new",
)({
  staticData: {
    crumb: (match: MakeRouteMatchUnion, t: TFunction) => {
      const params = match.params as {
        lang: string;
        org: string;
        location: string;
      };
      return [
        {
          label: t("page.webhookEndpoints.crumb"),
          to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints",
          params,
        },
        { label: t("page.webhookEndpoints.new.crumb") },
      ];
    },
  },
  component: RouteComponent,
});

type CreatedEndpoint = {
  id: string;
  url: string;
  signing_secret: string;
};

function RouteComponent() {
  const { lang, org, location } = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation({
    lang,
    namespace: TRANSLATION_NAMESPACE.warehouse,
  });

  const client = useMemo(() => apiClient(), []);

  const orgId = AuthUtils.getOrgIdByStub(authState, org) ?? org;
  const warehouseId = AuthUtils.getWarehouseId(authState, orgId, location);
  const idsReady = typeof orgId === "string" && typeof warehouseId === "string";

  const catalogQuery = client.useQuery(
    "get",
    "/{orgId}/warehouses/{warehouseId}/event-catalog",
    {
      params: {
        path: { orgId: orgId!, warehouseId: warehouseId! },
      },
    },
    { enabled: idsReady },
  );

  const createMutation = client.useMutation(
    "post",
    "/{orgId}/warehouses/{warehouseId}/endpoints",
  );

  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");
  const [subscriptions, setSubscriptions] = useState<string[]>([]);
  const [created, setCreated] = useState<CreatedEndpoint | undefined>();

  const eventItems = useMemo(
    () => catalogQuery.data?.data ?? [],
    [catalogQuery.data],
  );

  const toggleSubscription = (type: string, isSelected: boolean) => {
    setSubscriptions((prev) =>
      isSelected ? [...prev, type] : prev.filter((t) => t !== type),
    );
  };

  const backToList = () =>
    navigate({
      to: "/$lang/orgs/$org/internal/whs/$location/webhooks/endpoints",
      params: { lang, org, location },
    });

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!idsReady || createMutation.isPending) return;

    createMutation
      .mutateAsync({
        params: { path: { orgId: orgId!, warehouseId: warehouseId! } },
        body: {
          url,
          ...(description ? { description } : {}),
          ...(subscriptions.length > 0 ? { subscriptions } : {}),
        },
      })
      .then((response) => {
        logger.info("Created webhook endpoint", { id: response.data.id });
        setCreated({
          id: response.data.id,
          url: response.data.url,
          signing_secret: response.data.signing_secret,
        });
      })
      .catch((error: unknown) => {
        logger.warn("Failed to create webhook endpoint", { error });
        const message =
          error instanceof Error
            ? error.message
            : t("page.webhookEndpoints.new.submitFailedFallback");
        toast.error(message);
      });
  };

  const copySecret = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.signing_secret);
      toast.success(t("page.webhookEndpoints.created.copiedToast"));
    } catch {
      toast.error(t("page.webhookEndpoints.created.copyFailedToast"));
    }
  };

  if (!idsReady) {
    return (
      <div className={`${palletOverrides.basicPageContent} ${styles.page}`}>
        <Loader type="page" />
      </div>
    );
  }

  if (created) {
    return (
      <div className={`${palletOverrides.basicPageContent} ${styles.page}`}>
        <header className={styles.header}>
          <h1 className={styles.title}>
            {t("page.webhookEndpoints.created.title")}
          </h1>
        </header>
        <Card>
          <div className={styles.createdBody}>
            <p className={styles.warningLine}>
              {t("page.webhookEndpoints.created.secretWarningLine1")}
            </p>
            <p className={styles.warningLine}>
              {t("page.webhookEndpoints.created.secretWarningLine2")}
            </p>
            <TextInput
              name="endpoint-url"
              label={t("page.webhookEndpoints.created.endpointUrlLabel")}
              value={created.url}
              isReadOnly={true}
            />
            <pre
              aria-label={t("page.webhookEndpoints.created.secretAriaLabel")}
              className={styles.secret}
            >
              {created.signing_secret}
            </pre>
            <div className={styles.actions}>
              <Button
                text={t("page.webhookEndpoints.created.copySecret")}
                onPress={copySecret}
              />
              <Button
                text={t("page.webhookEndpoints.created.done")}
                variant="secondary"
                onPress={() => backToList()}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`${palletOverrides.basicPageContent} ${styles.page}`}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t("page.webhookEndpoints.new.title")}</h1>
        <p className={styles.subtitle}>
          {t("page.webhookEndpoints.new.intro")}
        </p>
      </header>

      <Form
        onSubmit={onSubmit}
        button={
          <div className={styles.actions}>
            <Button
              type="submit"
              text={
                createMutation.isPending
                  ? t("page.webhookEndpoints.new.submitting")
                  : t("page.webhookEndpoints.new.submit")
              }
              isDisabled={createMutation.isPending || url.trim().length === 0}
            />
            <Button
              type="button"
              variant="tertiary"
              text={t("page.webhookEndpoints.new.cancel")}
              onPress={() => backToList()}
            />
          </div>
        }
      >
        <TextInput
          name="url"
          label={t("page.webhookEndpoints.new.urlLabel")}
          type="text"
          placeholder={t("page.webhookEndpoints.new.urlPlaceholder")}
          value={url}
          onChange={(ev) => setUrl(ev.target.value)}
          isRequired={true}
          autofocus={true}
        />

        <TextInput
          name="description"
          label={t("page.webhookEndpoints.new.descriptionLabel")}
          placeholder={t("page.webhookEndpoints.new.descriptionPlaceholder")}
          value={description}
          onChange={(ev) => setDescription(ev.target.value)}
        />

        {catalogQuery.isLoading ? (
          <Loader type="page" />
        ) : catalogQuery.isError ? (
          <AppError
            description={t("page.webhookEndpoints.new.catalogFailed")}
            resolution={t("page.webhookEndpoints.new.catalogFailedResolution")}
          />
        ) : eventItems.length === 0 ? null : (
          <div
            className={styles.subscriptionGroup}
            role="group"
            aria-label={t("page.webhookEndpoints.new.subscriptionsLabel")}
          >
            <div className={styles.subscriptionGroupLabel}>
              {t("page.webhookEndpoints.new.subscriptionsLabel")}
            </div>
            <ul className={styles.subscriptionList}>
              {eventItems.map((entry) => {
                const selected = subscriptions.includes(entry.type);
                return (
                  <li key={entry.type} className={styles.subscriptionItem}>
                    <Checkbox
                      name="subscriptions"
                      value={entry.type}
                      label={entry.type}
                      isSelected={selected}
                      onChange={(next) => toggleSubscription(entry.type, next)}
                    />
                    {entry.description ? (
                      <p className={styles.subscriptionItemDescription}>
                        {entry.description}
                      </p>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Form>
    </div>
  );
}
